import { isNil } from 'lodash';
import {
  Flow,
  FlowItem,
  FlowItemParameterRef,
  FlowItemRef,
  FlowItemSection,
  FlowItemSharedRef,
  RuleResponseLevel,
  RuleValidationResponse,
} from '../model/flow.types';

export interface CheckContiguousItemByTypeOptions<T = number | string> {
  flow: Flow;
  itemTypeRef: FlowItemRef;
  parameterTypeRef: FlowItemParameterRef;
  primaryTypes: T[];
  followedByTypes?: T[];
  followedByStartOffset?: number;
  precededByTypes?: T[];
  minRequired: number;
  suffixMessage: string;
  sectionBreaksSequence?: (item: FlowItemSection) => boolean;
}

const isType = (
  itemTypeRef: FlowItemRef,
  parameterTypeRef: FlowItemParameterRef,
  flowItem: FlowItem,
  value: string | number
): boolean => {
  if (flowItem.ref !== itemTypeRef) {
    return false;
  }

  const idx = flowItem.parameters.findIndex(
    (p) => p.ref === parameterTypeRef && p.value === value
  );

  return idx !== -1;
};

const isOneOfTypes = (
  itemTypeRef: FlowItemRef,
  parameterTypeRef: FlowItemParameterRef,
  types: (string | number)[],
  item: FlowItem | undefined
): boolean => {
  if (item) {
    return types.reduce((val: boolean, curr: string | number): boolean => {
      return val || isType(itemTypeRef, parameterTypeRef, item, curr);
    }, false);
  }
  return false;
};

export const flowRuleCheckContiguousByType = (
  options: CheckContiguousItemByTypeOptions
): RuleValidationResponse | undefined => {
  const {
    flow,
    itemTypeRef,
    parameterTypeRef,
    primaryTypes,
    minRequired,
    suffixMessage,
    followedByTypes,
    precededByTypes,
    followedByStartOffset,
    sectionBreaksSequence,
  } = options;

  let contiguous = 0;

  const items = flow.items;

  let offsetIndexBy = 0;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    if (
      item.ref === FlowItemSharedRef.SECTION &&
      (isNil(sectionBreaksSequence) || !sectionBreaksSequence(item))
    ) {
      offsetIndexBy++;
      continue;
    } else {
      offsetIndexBy = 0;
    }

    const actualIndex = i - offsetIndexBy;

    const isPrimaryType = isOneOfTypes(
      itemTypeRef,
      parameterTypeRef,
      primaryTypes,
      item
    );
    const isFollowedByType =
      isOneOfTypes(
        itemTypeRef,
        parameterTypeRef,
        followedByTypes ?? [],
        item
      ) &&
      contiguous > 0 &&
      contiguous >= (followedByStartOffset ?? 0);

    const isPrecededByType =
      isOneOfTypes(
        itemTypeRef,
        parameterTypeRef,
        precededByTypes ?? [],
        items[actualIndex - 1]
      ) &&
      isPrimaryType &&
      contiguous === 0;

    if (isPrecededByType) {
      // bump contiguous an extra time if we are the primary type and were preceded
      // by a valid type
      contiguous++;
    }

    const isContigousType = isPrimaryType || isFollowedByType;

    if (isContigousType) {
      contiguous++;
    }

    const isLastItem = items.length - 1 === i;

    if (!isContigousType || isLastItem) {
      if (contiguous > 0 && contiguous < minRequired) {
        const remaining = minRequired - contiguous;

        const startIndex = isLastItem ? i : actualIndex - contiguous;

        return {
          level: RuleResponseLevel.ERROR,
          message: `${remaining} ${suffixMessage}`,
          startIndex,
          endIndex: i,
          startItemId: items[startIndex].id as string,
          endItemId: items[i].id as string,
        };
      }
      contiguous = 0;
    }
  }
};
